import dayjs from 'src/utils/dayjs';
import { red, grey } from '@material-ui/core/colors';
import { TEAL_PRIMARY } from 'src/utils/Style/Theme';
import {
  AddToAssortmentItem,
  AddToAssortmentStatus,
  MinimalItem,
  PlanItem,
  PlanItemStatus,
} from 'src/pages/AssortmentBuild/Planning.types';
import { reverse, sortBy } from 'lodash';
import { ParsedCartData, ParsedPlanData, ProcessedCartData, ProcessedPlanData } from './PlanQueue.types';

export function mapPlanItem(item: PlanItem): MinimalItem {
  return {
    jobId: item.jobId,
    primaryText: item.description ?? item.id,
    updatedAt: item.updatedAt,
    state: item.status,
  };
}

const ASST_TIME_FORMAT = 'h:mma - MM/DD/YYYY';
export function formatAsstTime(time: string) {
  return dayjs(time).format(ASST_TIME_FORMAT);
}

export const getIconBadgeColor = (items: {
  addProcessing?: unknown[];
  addFailed?: unknown[];
  planPending?: unknown[];
  planQueued?: unknown[];
  planProcessing?: unknown[];
  planPredicting?: unknown[];
  planCompleted?: unknown[];
  planFailed?: unknown[];
  /** Returns color as a hex value instead of the material-ui `color` value  */
  cssColorFormat?: boolean;
}) => {
  const {
    addProcessing = [],
    addFailed = [],
    planPending = [],
    planProcessing = [],
    planCompleted = [],
    planFailed = [],
    cssColorFormat = false,
  } = items;

  if (
    addProcessing.length === 0 &&
    addFailed.length === 0 &&
    planPending.length === 0 &&
    planProcessing.length === 0 &&
    planCompleted.length === 0 &&
    planFailed.length === 0
  ) {
    return cssColorFormat ? grey[500] : 'default';
  }

  if (planProcessing.length > 0 || addProcessing.length > 0) {
    return cssColorFormat ? TEAL_PRIMARY : 'secondary';
  } else if (planFailed.length > 0 || addFailed.length > 0) {
    return cssColorFormat ? 'red' : red[500];
  }

  // teal for planPending/planCompleted
  return cssColorFormat ? TEAL_PRIMARY : 'secondary';
};

const getPlanItemsByStatus = (items: PlanItem[], filterStatus: PlanItemStatus) => {
  return items.filter(({ status }) => status === filterStatus);
};

export const parsePlanItems = (items: PlanItem[]): ParsedPlanData => {
  return {
    pending: getPlanItemsByStatus(items, PlanItemStatus.Pending),
    queued: getPlanItemsByStatus(items, PlanItemStatus.Queued),
    processing: getPlanItemsByStatus(items, PlanItemStatus.Processing),
    predicting: getPlanItemsByStatus(items, PlanItemStatus.Predicting),
    completed: getPlanItemsByStatus(items, PlanItemStatus.Completed),
    failed: getPlanItemsByStatus(items, PlanItemStatus.Failed),
  };
};

const getAddItemsByStatus = (items: AddToAssortmentItem[], filterStatus: AddToAssortmentStatus) => {
  return items.filter(({ status }) => status === filterStatus);
};

const parseAddItems = (items: AddToAssortmentItem[]): ParsedCartData => {
  return {
    processing: getAddItemsByStatus(items, AddToAssortmentStatus.Processing),
    failed: getAddItemsByStatus(items, AddToAssortmentStatus.Failed),
  };
};

export function parseCartPlanItems(addToAsstItems: AddToAssortmentItem[]) {
  const { processing, failed } = parseAddItems(addToAsstItems);
  return {
    processing,
    failed,
  };
}

function formatPlanData(items: {
  pending: PlanItem[];
  queued: PlanItem[];
  processing: PlanItem[];
  predicting: PlanItem[];
  completed: PlanItem[];
  failed: PlanItem[];
}): ProcessedPlanData {
  const { pending, queued, processing, predicting, completed, failed } = items;
  return {
    pending: {
      items: reverse(sortBy(pending.map(mapPlanItem), 'updatedAt')),
      badgeColor: getIconBadgeColor({ planPending: pending, cssColorFormat: true }),
    },
    queued: {
      items: reverse(sortBy(queued.map(mapPlanItem), 'updatedAt')),
      badgeColor: getIconBadgeColor({ planQueued: queued, cssColorFormat: true }),
    },
    processing: {
      items: reverse(sortBy(processing.map(mapPlanItem), 'updatedAt')),
      badgeColor: getIconBadgeColor({ planProcessing: processing, cssColorFormat: true }),
    },
    predicting: {
      items: reverse(sortBy(predicting.map(mapPlanItem), 'updatedAt')),
      badgeColor: getIconBadgeColor({ planPredicting: predicting, cssColorFormat: true }),
    },
    completed: {
      items: reverse(sortBy(completed.map(mapPlanItem), 'updatedAt')),
      badgeColor: getIconBadgeColor({ planCompleted: completed, cssColorFormat: true }),
    },
    failed: {
      items: reverse(sortBy(failed.map(mapPlanItem), 'updatedAt')),
      badgeColor: getIconBadgeColor({ planFailed: failed, cssColorFormat: true }),
    },
  };
}

function formatCartData(items: {
  addProcessing: AddToAssortmentItem[];
  addFailed: AddToAssortmentItem[];
}): ProcessedCartData {
  const { addProcessing, addFailed } = items;
  return {
    processing: {
      items: addProcessing.map(
        (item: AddToAssortmentItem): MinimalItem => ({
          primaryText: `Adding ${item.masterCount} items - ${formatAsstTime(item.updatedAt)}`,
          updatedAt: item.updatedAt,
        }),
      ),
      badgeColor: getIconBadgeColor({ addProcessing, cssColorFormat: true }),
    },
    failed: {
      items: addFailed.map(
        (item: AddToAssortmentItem): MinimalItem => ({
          primaryText: item.cartId,
          updatedAt: item.updatedAt,
        }),
      ),
      badgeColor: getIconBadgeColor({ addFailed, cssColorFormat: true }),
    },
  };
}

export function processPlanItems(planItems: PlanItem[]): ProcessedPlanData {
  const { pending, queued, processing, predicting, completed, failed } = parsePlanItems(planItems);
  return formatPlanData({
    pending,
    queued,
    processing,
    predicting,
    completed,
    failed,
  });
}

export function processCartItems(addToAsstItems: AddToAssortmentItem[]): ProcessedCartData {
  const { processing, failed } = parseCartPlanItems(addToAsstItems);
  return formatCartData({ addProcessing: processing, addFailed: failed });
}
